/**
 * Admin Authentication Context
 *
 * Handles admin authentication with JWT tokens stored in localStorage.
 * Separate from customer auth to allow both to coexist.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import type { AdminUser } from '@petmas/shared';
import { apiClient } from '../api';

// Storage keys (prefixed with admin_ to separate from customer auth)
const ADMIN_TOKEN_KEY = 'petmas_admin_token';
const ADMIN_REFRESH_TOKEN_KEY = 'petmas_admin_refresh_token';
const ADMIN_TOKEN_EXPIRES_KEY = 'petmas_admin_token_expires';
const ADMIN_USER_KEY = 'petmas_admin_user';

const REFRESH_BUFFER_MS = 60_000; // Refresh 1 minute before expiration

interface AdminAuthContextType {
  admin: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isSuperAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshTimerRef = useRef<number | null>(null);

  const setupRefreshTimer = () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const expiresAt = localStorage.getItem(ADMIN_TOKEN_EXPIRES_KEY);
    if (!expiresAt) return;

    const expirationTime = parseInt(expiresAt, 10);
    const now = Date.now();
    const timeUntilRefresh = expirationTime - now - REFRESH_BUFFER_MS;

    if (timeUntilRefresh <= 0) {
      void refreshAccessToken();
      return;
    }

    refreshTimerRef.current = window.setTimeout(() => {
      void refreshAccessToken();
    }, timeUntilRefresh);
  };

  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem(ADMIN_REFRESH_TOKEN_KEY);
      if (!refreshToken) {
        await logout();
        return;
      }

      const data = await apiClient.adminRefreshToken(refreshToken);
      localStorage.setItem(ADMIN_TOKEN_KEY, data.accessToken);
      localStorage.setItem(ADMIN_TOKEN_EXPIRES_KEY, String(data.expiresAt));
      setupRefreshTimer();
    } catch (error) {
      console.error('Admin token refresh failed:', error);
      await logout();
    }
  };

  const login = async (email: string, password: string) => {
    const data = await apiClient.adminLogin(email, password);
    const { admin: adminData, accessToken, refreshToken, expiresAt } = data;

    localStorage.setItem(ADMIN_TOKEN_KEY, accessToken);
    localStorage.setItem(ADMIN_REFRESH_TOKEN_KEY, refreshToken);
    localStorage.setItem(ADMIN_TOKEN_EXPIRES_KEY, String(expiresAt));
    localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(adminData));

    setAdmin(adminData as AdminUser);
    setupRefreshTimer();
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem(ADMIN_TOKEN_KEY);
      const refreshToken = localStorage.getItem(ADMIN_REFRESH_TOKEN_KEY);

      if (token && refreshToken) {
        await apiClient.adminLogout(token, refreshToken).catch(() => {}); // Ignore errors on logout
      }
    } finally {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      localStorage.removeItem(ADMIN_REFRESH_TOKEN_KEY);
      localStorage.removeItem(ADMIN_TOKEN_EXPIRES_KEY);
      localStorage.removeItem(ADMIN_USER_KEY);

      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }

      setAdmin(null);
    }
  };

  // Initialize from localStorage
  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem(ADMIN_TOKEN_KEY);
        const storedAdmin = localStorage.getItem(ADMIN_USER_KEY);
        const expiresAt = localStorage.getItem(ADMIN_TOKEN_EXPIRES_KEY);

        if (!token || !storedAdmin || !expiresAt) {
          setIsLoading(false);
          return;
        }

        // Check if token is expired
        if (Date.now() > parseInt(expiresAt, 10)) {
          await refreshAccessToken();
        } else {
          setAdmin(JSON.parse(storedAdmin));
          setupRefreshTimer();
        }
      } catch (error) {
        console.error('Admin auth init error:', error);
        await logout();
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  return (
    <AdminAuthContext.Provider
      value={{
        admin,
        isAuthenticated: admin !== null,
        isLoading,
        isSuperAdmin: admin?.role === 'superadmin',
        login,
        logout,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth(): AdminAuthContextType {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
}

// Helper to get admin token for API calls
export function getAdminToken(): string | null {
  return localStorage.getItem(ADMIN_TOKEN_KEY);
}
